import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, Phone } from "lucide-react";
import axios from "axios";
import { decryptRates } from "../utils/crypto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = "+17787590400";

const LossChart = ({ losses }) => {
  const minLoss = Math.min(...losses);
  const maxLoss = Math.max(...losses);
  const range = maxLoss - minLoss || 1;
  
  const points = losses.map((loss, i) => {
    const x = (i / (losses.length - 1)) * 100;
    const normalized = (loss - minLoss) / range;
    const y = 15 + (1 - normalized) * 70;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="w-28 h-8">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="lossGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgb(239, 68, 68)" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="rgb(239, 68, 68)" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon 
          points={`0,100 ${points} 100,100`}
          fill="url(#lossGradient)"
        />
        <polyline
          points={points}
          fill="none"
          stroke="rgb(239, 68, 68)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const BANKS = [
  { id: "scotiabank", name: "Scotiabank", logo: "/logos/scotiabank.jpg" },
  { id: "rbc", name: "RBC", logo: "/logos/rbc.jpg" },
  { id: "td", name: "TD Bank", logo: "/logos/td.png" },
  { id: "bmo", name: "BMO", logo: "/logos/bmo.png" },
  { id: "cibc", name: "CIBC", logo: "/logos/cibc.png" }
];

const getCurrencyFlag = (code) => {
  const flags = {
    USD: "/flags/USD.png", EUR: "/flags/EUR.png", GBP: "/flags/GBP.png",
    JPY: "/flags/JPY.png", AUD: "/flags/AUD.png", CHF: "/flags/CHF.png",
    CNY: "/flags/CNY.png", INR: "/flags/INR.png", MXN: "/flags/MXN.png",
  };
  return flags[code] || null;
};

const ALLOWED_CURRENCIES = ['USD', 'EUR', 'GBP'];

export default function BankComparison() {
  const [ourRates, setOurRates] = useState([]);
  const [bankRates, setBankRates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [amount, setAmount] = useState(1000);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => { fetchAllRates(); }, []);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown, handleClickOutside]);

  const fetchAllRates = async () => {
    setLoading(true);
    try {
      const [ourResponse, bankResponse] = await Promise.all([
        axios.get(`${API}/x7k9`),
        axios.get(`${API}/b4nk`)
      ]);
      const decryptedOurRates = decryptRates(ourResponse.data.d);
      const decryptedBankRates = decryptRates(bankResponse.data.d);
      setOurRates(decryptedOurRates.filter(r => !r.is_crypto && ALLOWED_CURRENCIES.includes(r.currency_code)));
      setBankRates(decryptedBankRates);
      if (decryptedBankRates.last_updated) {
        setLastUpdated(new Date(decryptedBankRates.last_updated));
      } else {
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Error fetching rates:", error);
    } finally {
      setLoading(false);
    }
  };

  const ourRate = ourRates.find(r => r.currency_code === selectedCurrency);
  
  const comparisons = useCallback(() => {
    if (!ourRate) return [];
    const customerReceivesWithUs = amount * ourRate.sell_rate;  // customer sells us the currency -> our 'we buy' price
    const customerPaysWithUs = amount * ourRate.buy_rate;      // customer buys from us -> our 'we sell' price
    
    return BANKS.map(bank => {
      const bankData = bankRates[bank.id]?.[selectedCurrency] || {};
      // Only use actual rates - no fallback
      const buyRate = bankData.buy;
      const sellRate = bankData.sell;
      
      if (!buyRate && !sellRate) return null;
      
      return {
        ...bank,
        buyRate,
        sellRate,
        customerReceives: buyRate ? amount * buyRate : null,
        customerPays: sellRate ? amount * sellRate : null,
        youLose: buyRate ? customerReceivesWithUs - (amount * buyRate) : null,
        youSave: sellRate ? (amount * sellRate) - customerPaysWithUs : null
      };
    }).filter(Boolean);
  }, [ourRate, amount, bankRates, selectedCurrency])();

  const customerReceivesWithUs = ourRate ? amount * ourRate.sell_rate : 0;
  const customerPaysWithUs = ourRate ? amount * ourRate.buy_rate : 0;

  const fmt = (n) => n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const allLosses = comparisons.map(c => c.youLose);

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000 / 60);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff} min ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return lastUpdated.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <section className="bg-[#001435] py-16" data-testid="bank-comparison">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 bg-white/10 rounded mx-auto"></div>
            <div className="h-4 w-48 bg-white/5 rounded mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#001435] py-16" data-testid="bank-comparison">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-3">
            Compare Exchange Rates
          </h2>
          <p className="text-white/50 text-base font-normal">
            See how much you save with FonderX
          </p>
        </div>

        {/* Input */}
        <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
          <span className="text-white/60 text-sm">I have</span>
          <div className="relative flex items-center">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
              className="w-28 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white text-lg text-center outline-none focus:border-white/40 transition-colors pr-8"
            />
            <div className="absolute right-2 flex flex-col gap-0.5">
              <button 
                onClick={() => setAmount(prev => prev + 100)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M6 0L12 8H0L6 0Z" fill="currentColor"/>
                </svg>
              </button>
              <button 
                onClick={() => setAmount(prev => Math.max(0, prev - 100))}
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M6 8L0 0H12L6 8Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2 hover:bg-white/15 transition-colors"
            >
              {getCurrencyFlag(selectedCurrency) && (
                <img src={getCurrencyFlag(selectedCurrency)} alt={`${selectedCurrency} flag`} className="w-5 h-5 rounded-full" />
              )}
              <span className="text-white">{selectedCurrency}</span>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showDropdown && (
              <div className="absolute top-full mt-2 left-0 w-44 bg-[#0a1f3d] border border-white/20 rounded-lg shadow-xl overflow-hidden z-50">
                {ourRates.map((rate) => (
                  <button
                    key={rate.currency_code}
                    onClick={() => { setSelectedCurrency(rate.currency_code); setShowDropdown(false); }}
                    className={`w-full px-4 py-2.5 flex items-center gap-2 hover:bg-white/10 transition-colors text-left text-sm ${selectedCurrency === rate.currency_code ? 'bg-white/10' : ''}`}
                  >
                    {getCurrencyFlag(rate.currency_code) && (
                      <img src={getCurrencyFlag(rate.currency_code)} alt={`${rate.currency_code} flag`} className="w-4 h-4 rounded-full" />
                    )}
                    <span className="text-white">{rate.currency_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Table - Desktop */}
        <div className="hidden md:block bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-6 gap-4 px-5 py-3 border-b border-white/10 bg-white/5">
            <div className="text-white text-xs uppercase tracking-wide">Provider</div>
            <div className="text-white text-xs uppercase tracking-wide text-right">Sell {selectedCurrency}<span className="block normal-case text-white/40 tracking-normal">you get (CAD)</span></div>
            <div className="text-white text-xs uppercase tracking-wide text-right">vs FonderX<span className="block normal-case text-white/40 tracking-normal">you get less</span></div>
            <div className="text-white text-xs uppercase tracking-wide text-right">Buy {selectedCurrency}<span className="block normal-case text-white/40 tracking-normal">you pay (CAD)</span></div>
            <div className="text-white text-xs uppercase tracking-wide text-right">vs FonderX<span className="block normal-case text-white/40 tracking-normal">you pay more</span></div>
            <div></div>
          </div>

          {/* FonderX Row */}
          {ourRate && (
            <div className="grid grid-cols-6 gap-4 px-5 py-4 border-b border-white/10 bg-[#00d9c0]/5 items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center shrink-0">
                  <img src="/logos/fonderx.png" alt="FonderX" className="w-full h-full object-cover" />
                </div>
                <span className="text-white font-normal">FonderX</span>
                <span className="bg-[#00d9c0] text-[#001435] text-[10px] font-medium px-1.5 py-0.5 rounded">BEST</span>
              </div>
              <div className="text-right">
                <p className="text-white text-base">${fmt(customerReceivesWithUs)}</p>
              </div>
              <div className="text-right"></div>
              <div className="text-right">
                <p className="text-white text-base">${fmt(customerPaysWithUs)}</p>
              </div>
              <div className="text-right"></div>
              <div></div>
            </div>
          )}

          {/* Bank Rows */}
          {comparisons.map((bank, i) => (
            <div 
              key={bank.id} 
              className={`grid grid-cols-6 gap-4 px-5 py-4 items-center hover:bg-white/5 transition-colors ${i < comparisons.length - 1 ? 'border-b border-white/5' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center">
                  <img src={bank.logo} alt={bank.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-white/80 font-normal">{bank.name}</span>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-base">${fmt(bank.customerReceives)}</p>
              </div>
              <div className="text-right">
                <p className="text-red-500 text-sm">-${fmt(bank.youLose)}</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-base">${fmt(bank.customerPays)}</p>
              </div>
              <div className="text-right">
                <p className="text-[#00d9c0] text-sm">+${fmt(bank.youSave)}</p>
              </div>
              <div>
                <LossChart losses={allLosses} />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {/* FonderX Card */}
          {ourRate && (
            <div className="bg-[#00d9c0]/10 border border-[#00d9c0]/20 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center shrink-0">
                  <img src="/logos/fonderx.png" alt="FonderX" className="w-full h-full object-cover" />
                </div>
                <span className="text-white font-normal">FonderX</span>
                <span className="bg-[#00d9c0] text-[#001435] text-[10px] font-medium px-1.5 py-0.5 rounded">BEST</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-white/40 text-xs mb-1">Sell {selectedCurrency} · you get</p>
                  <p className="text-white">${fmt(customerReceivesWithUs)}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs mb-1">Buy {selectedCurrency} · you pay</p>
                  <p className="text-white">${fmt(customerPaysWithUs)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bank Cards */}
          {comparisons.map((bank) => (
            <div key={bank.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center">
                  <img src={bank.logo} alt={bank.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-white/80 font-normal">{bank.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-white/40 text-xs mb-1">Sell {selectedCurrency} · you get</p>
                  <p className="text-white/80">${fmt(bank.customerReceives)}</p>
                  <p className="text-red-500 text-xs">You lose ${fmt(bank.youLose)}</p>
                </div>
                <div>
                  <p className="text-white/40 text-xs mb-1">Buy {selectedCurrency} · you pay</p>
                  <p className="text-white/80">${fmt(bank.customerPays)}</p>
                  <p className="text-[#00d9c0] text-xs">You save ${fmt(bank.youSave)}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10">
                <LossChart losses={allLosses} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col items-center gap-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00d9c0]"></span>
            <span className="text-white/70 text-xs">
              Bank rates last updated: <span className="font-semibold text-white/90">{formatLastUpdated()}</span>
              {lastUpdated && (
                <span className="text-white/40">
                  {" "}({lastUpdated.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})
                </span>
              )}
            </span>
          </div>
          <p className="text-white/30 text-xs text-center">Actual rates may vary</p>
        </div>
      </div>
    </section>
  );
}
